/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './TrustedContactsGlobal.scss';

import classnames from 'classnames';
import isNil from 'lodash/isNil';
import { array, func, object, string } from 'prop-types';
import React, { Fragment } from 'react';

import AccountDisplay from 'components/shared/AccountDisplay/AccountDisplay';

const GroupHeader = ({ account, accountIdMap, onChange }) => {
  const { groupingClassName, name } = account;
  const isToggled = accountIdMap[name.accountId];

  return (
    <div
      className={`trusted-contacts-global-group-header-container ${groupingClassName}`}>
      <input
        className="trusted-contacts-global-account-checkbox"
        checked={isToggled}
        name={name.accountId}
        onChange={() => onChange(name.accountId)}
        value={name.accountId}
        type="checkbox"
      />
      <AccountDisplay account={name} withType={true} />
    </div>
  );
};

const GroupRow = ({
  account,
  accountIdMap,
  accountIdTrustedContactsMap,
  currentName,
  onChange
}) => {
  const { name, trustedContacts } = account;
  const isToggled = accountIdMap[name.accountId];
  const hasTrustedContacts = Boolean(trustedContacts);
  const rowClass = classnames('trusted-contacts-global-row', {
    'disabled-contacts': !isToggled
  });

  return (
    <div className={rowClass}>
      {hasTrustedContacts ? (
        <HasTrustedContacts
          account={account}
          accountIdTrustedContactsMap={accountIdTrustedContactsMap}
          currentName={currentName}
          isToggled={isToggled}
          onChange={onChange}
        />
      ) : (
        isToggled && (
          <TrustedContact
            account={account}
            accountIdTrustedContactsMap={accountIdTrustedContactsMap}
            currentName={currentName}
            onChange={onChange}
          />
        )
      )}
    </div>
  );
};

const HasTrustedContacts = ({
  account,
  accountIdTrustedContactsMap,
  currentName,
  isToggled,
  onChange
}) => {
  const [contactOne, contactTwo = null] = [...account.trustedContacts];
  const trustedContacts = [contactOne, contactTwo];

  return isToggled
    ? trustedContacts.map((contact, key) => (
        <TrustedContact
          account={account}
          accountIdTrustedContactsMap={accountIdTrustedContactsMap}
          contact={contact}
          currentName={currentName}
          key={key}
          onChange={onChange}
        />
      ))
    : trustedContacts.map((contact, key) => (
        <DisabledTrustedContact account={account} contact={contact} key={key} />
      ));
};

const TrustedContact = ({
  account,
  accountIdTrustedContactsMap,
  contact,
  currentName,
  onChange
}) => {
  let contactState, displayName, sequenceNumber;
  const { name } = account;
  const { accountId } = name;
  const accountTrustedContacts = accountIdTrustedContactsMap[accountId];

  if (isNil(contact)) {
    sequenceNumber = null;
    contactState = accountTrustedContacts.find(
      (trustedContact) => trustedContact.sequenceNumber === sequenceNumber
    );
    displayName = `Add ${currentName}`;
  } else {
    const { firstName, lastName } = contact;
    sequenceNumber = contact.sequenceNumber;
    contactState = accountTrustedContacts.find(
      (trustedContact) => trustedContact.sequenceNumber === sequenceNumber
    );
    const defaultName = `${firstName} ${lastName}`;
    displayName = contactState.active
      ? `Replacing ${defaultName} with ${currentName}`
      : defaultName;
  }

  return (
    <Fragment>
      <label htmlFor={sequenceNumber}>
        <input
          checked={contactState.active}
          className="trusted-contacts-global-input"
          htmlFor={sequenceNumber}
          name={accountId}
          onChange={() => onChange(accountId, contactState)}
          type="radio"
        />
        <span className="blocked_data">{displayName}</span>
      </label>
    </Fragment>
  );
};

const DisabledTrustedContact = ({ account, contact }) => {
  let displayName, sequenceNumber;
  const { name } = account;
  const { accountId } = name;

  if (isNil(contact)) {
    sequenceNumber = null;
    displayName = 'Add Trusted Contact';
  } else {
    const { firstName, lastName } = contact;
    sequenceNumber = contact.sequenceNumber;
    displayName = `${firstName} ${lastName}`;
  }
  return (
    <Fragment>
      <label htmlFor={sequenceNumber}>
        <input
          className="trusted-contacts-global-input"
          disabled={true}
          htmlFor={sequenceNumber}
          name={accountId}
          type="radio"
        />
        <span className="blocked_data">{displayName}</span>
      </label>
    </Fragment>
  );
};

const TrustedContactRow = ({
  account,
  accountIdMap,
  accountIdTrustedContactsMap,
  currentName,
  onAccountGroupChange,
  onTrustedContactChange
}) => (
  <div className="trusted-contacts-global-row-container">
    <GroupHeader
      account={account}
      accountIdMap={accountIdMap}
      onChange={onAccountGroupChange}
    />
    <GroupRow
      account={account}
      accountIdMap={accountIdMap}
      accountIdTrustedContactsMap={accountIdTrustedContactsMap}
      currentName={currentName}
      onChange={onTrustedContactChange}
    />
  </div>
);

const TrustedContactsGlobal = ({
  accountIdMap,
  accountIdTrustedContactsMap,
  currentName,
  handleClearAll,
  handleSelectAll,
  onAccountGroupChange,
  onTrustedContactChange,
  trustedContactMappedGroups
}) => (
  <div className="trusted-contacts-global-contact">
    <div className="trusted-contacts-global-toggle-all-buttons">
      <a className="action-link" onClick={handleSelectAll}>
        Select All
      </a>
      <a className="action-link" onClick={handleClearAll}>
        Clear All
      </a>
    </div>
    <div className="trusted-contacts-global-content">
      {trustedContactMappedGroups.map((account, key) => (account.isEditable &&
        <TrustedContactRow
          key={key}
          account={account}
          accountIdMap={accountIdMap}
          accountIdTrustedContactsMap={accountIdTrustedContactsMap}
          currentName={currentName}
          onAccountGroupChange={onAccountGroupChange}
          onTrustedContactChange={onTrustedContactChange}
        />
      ))}
    </div>
  </div>
);

TrustedContactsGlobal.propTypes = {
  accountIdMap: object,
  accountIdTrustedContactsMap: object,
  currentName: string,
  handleClearAll: func,
  handleSelectAll: func,
  onAccountGroupChange: func,
  onTrustedContactChange: func,
  trustedContactMappedGroups: array
};

export default TrustedContactsGlobal;
